import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Box, Typography, Button, Tooltip, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
} from '@mui/material';
import { FileCopyOutlined as CopyIcon, Close as CloseIcon } from '@material-ui/icons';
import { useAuth } from '../../context/auth';
import { TOAST_TYPES } from '../../utils/constants';
import { toast } from '../../utils/utils';
import { submitContactUsRequest } from '../../services/misc.service';
import { getConsentSetupService } from '../../services/client.service';
import { isClientWorkspace } from '../../utils/capabilities';
import { getConsentPathway } from './components/consentPathways.config';

const ORANGE = '#FF6900';
const SUPPORT_EMAIL = 'contact@dqforall.com';
// "Reason for contact" options — a structured field so the team can triage and
// route messages instantly instead of parsing free-text intent.
const CONTACT_REASONS = ['General question', 'Technical support', 'Billing', 'Partnership'];
const MESSAGE_MAX = 500;

const cardSx = {
    p: { xs: 2.5, sm: 3.5 },
    border: `1px solid ${ORANGE}`,
    borderRadius: '14px',
    backgroundColor: 'var(--brand-surface, #FFF9F5)',
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'stretch', sm: 'center' },
    justifyContent: 'space-between',
    gap: { xs: 2, sm: 3 },
};

const actionBtnSx = {
    backgroundColor: ORANGE,
    color: '#fff',
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '14px',
    px: 3,
    py: 1,
    borderRadius: '10px',
    boxShadow: 'none',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    '&:hover': { backgroundColor: '#e55e00', boxShadow: 'none', color: '#fff' },
};

/**
 * Parental Consent — the School / School Network Support card (SNW, SW Parental
 * Consent Flow, 5 Oct 2026, slides 5, 7, 22, 23, 26, 36). The status line reads
 * "Not set up yet", "Set by <network>: <method>" while a school still has only
 * its network's assignment, and "Your method: <method>" once confirmed.
 */
const ParentalConsentCard = () => {
    const history = useHistory();
    const [setup, setSetup] = useState(null);
    useEffect(() => {
        let alive = true;
        getConsentSetupService()
            .then((res) => { if (alive) setSetup(res?.data || null); })
            .catch(() => { /* the card still renders, as not set up */ });
        return () => { alive = false; };
    }, []);

    // `consent_pathway` is the method that applies; `inherited` says it is the
    // network's (13 Oct 2026 — a school does not have to set it again).
    const applies = getConsentPathway(setup?.consent_pathway);
    const inherited = !!applies && setup?.inherited === true;
    const status = !applies
        ? 'Not set up yet'
        : inherited
            ? `Set by ${setup?.network?.org_name || 'your network'}: ${applies.title}`
            : `Your method: ${applies.title}`;

    return (
        <Box sx={cardSx}>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 700, color: 'var(--brand-text, #111827)', mb: 0.5 }}>
                    Parental Consent
                </Typography>
                <Typography sx={{ fontSize: '13.5px', color: 'var(--brand-text-muted, #6B7280)', lineHeight: 1.55 }}>
                    Review how parental consent is collected for your learners, and download the consent form templates.
                </Typography>
                <Typography sx={{ fontSize: '13.5px', fontWeight: 600, mt: 1, color: applies ? 'var(--brand-text, #374151)' : 'var(--brand-text-muted, #8A8F98)' }}>
                    {status}
                </Typography>
            </Box>
            <Button onClick={() => history.push('/dq_pro/parental-consent?from=support')} sx={actionBtnSx}>
                {applies ? 'View Parental Consent' : 'Set Up Parental Consent'}
            </Button>
        </Box>
    );
};

const Support = () => {
    const { authUser } = useAuth();
    // School and School Network workspaces show only the Parental Consent card
    // (SNW, SW Parental Consent Flow, 5 Oct 2026). Partner keeps its own cards.
    const isSchoolWorkspace = isClientWorkspace(authUser);

    const [copied, setCopied] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [firstName, setFirstName] = useState(authUser?.first_name || '');
    const [lastName, setLastName] = useState(authUser?.last_name || '');
    const [email, setEmail] = useState(authUser?.email || '');
    const [reason, setReason] = useState(CONTACT_REASONS[0]);
    const [message, setMessage] = useState('');

    const handleCopyEmail = async () => {
        try {
            await navigator.clipboard.writeText(SUPPORT_EMAIL);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast(TOAST_TYPES.WARN, 'Copy', `Please copy manually: ${SUPPORT_EMAIL}`);
        }
    };

    const openForm = () => {
        // Refresh prefill from the signed-in user each time the form opens.
        setFirstName(authUser?.first_name || '');
        setLastName(authUser?.last_name || '');
        setEmail(authUser?.email || '');
        setReason(CONTACT_REASONS[0]);
        setMessage('');
        setFormOpen(true);
    };

    const handleSubmit = async () => {
        const fullName = `${firstName} ${lastName}`.trim();
        if (!fullName || !email.trim() || !message.trim()) {
            toast(TOAST_TYPES.WARN, 'Contact Us', 'Please fill in your name, email and message.');
            return;
        }
        setSubmitting(true);
        try {
            const res = await submitContactUsRequest(fullName, email.trim(), reason, message.trim());
            if (res === false) {
                toast(TOAST_TYPES.DANGER, 'Contact Us', 'Could not send your message. Please try again.');
                return;
            }
            toast(TOAST_TYPES.SUCCESS, 'Message Sent', "Thanks! Our support team will get back to you soon.");
            setFormOpen(false);
            setMessage('');
        } catch (err) {
            toast(TOAST_TYPES.DANGER, 'Contact Us', err?.message || 'Could not send your message.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{
            px: 0,
            py: 5,
            maxWidth: '700px',
            mx: 'auto',
            minHeight: '60vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
        }}>
            <Typography sx={{ textAlign: 'center', color: ORANGE, fontSize: { xs: '28px', sm: '36px' }, fontWeight: 700, mb: 1 }}>
                Support
            </Typography>
            <Typography sx={{ textAlign: 'center', color: 'var(--brand-text, #374151)', fontSize: { xs: '15px', sm: '16px' }, mb: 5 }}>
                We're here to help. Reach out to our team and we'll get back to you as soon as possible.
            </Typography>

            {isSchoolWorkspace ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <ParentalConsentCard />
            </Box>
            ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* Contact Us */}
                <Box sx={cardSx}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '16px', fontWeight: 700, color: 'var(--brand-text, #111827)', mb: 0.5 }}>
                            Contact Us
                        </Typography>
                        <Typography sx={{ fontSize: '13.5px', color: 'var(--brand-text-muted, #6B7280)', lineHeight: 1.55 }}>
                            Have a question or need help? Send us a message and our support team will assist you.
                        </Typography>
                        {/* Copyable support email */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                            <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: ORANGE }}>
                                {SUPPORT_EMAIL}
                            </Typography>
                            <Tooltip title={copied ? 'Copied!' : 'Copy email'} arrow>
                                <IconButton size="small" onClick={handleCopyEmail} sx={{ color: ORANGE }}>
                                    <CopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                    <Button onClick={openForm} sx={actionBtnSx}>
                        Contact Us
                    </Button>
                </Box>

                {/* The Parental Consent Form and School Authorization Form templates
                    moved out of the Partner Support page (SNW, SW Parental Consent
                    Flow, slide 44 — "Remove from PW"). Schools and networks now
                    download them from the Parental Consent set-up itself. */}
                {/* Promotion Deck restored with a Contact Us CTA instead of a
                    Download — the deck is being redeveloped, so users request it
                    from the support team for now (per Lydia, Support of P0). */}
                <Box sx={cardSx}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '16px', fontWeight: 700, color: 'var(--brand-text, #111827)', mb: 0.5 }}>
                            Child Online Safety Assessment Promotion Deck
                        </Typography>
                        <Typography sx={{ fontSize: '13.5px', color: 'var(--brand-text-muted, #6B7280)', lineHeight: 1.55 }}>
                            A template deck to help you promote the Child Online Safety Assessments to your clients. Contact us to request it.
                        </Typography>
                    </Box>
                    <Button onClick={openForm} sx={actionBtnSx}>
                        Contact Us
                    </Button>
                </Box>
            </Box>
            )}

            {/* Contact Us form */}
            <Dialog open={formOpen} onClose={() => !submitting && setFormOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography component="div" sx={{ fontSize: '20px', fontWeight: 700, color: 'var(--brand-text, #111827)' }}>
                                Get in touch
                            </Typography>
                            <Typography sx={{ fontSize: '13px', color: 'var(--brand-text-muted, #6B7280)', fontWeight: 400, mt: 0.25 }}>
                                We'll get back to you within 1 business day.
                            </Typography>
                        </Box>
                        <IconButton onClick={() => setFormOpen(false)} disabled={submitting} size="small">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <TextField
                            label="First name"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Last name"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            fullWidth
                            size="small"
                        />
                    </Box>
                    <TextField
                        label="Email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        fullWidth
                        size="small"
                        required
                    />
                    <TextField
                        label="Reason for contact"
                        select
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        fullWidth
                        size="small"
                    >
                        {CONTACT_REASONS.map((r) => (
                            <MenuItem key={r} value={r}>{r}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX))}
                        fullWidth
                        multiline
                        minRows={4}
                        placeholder="How can we help?"
                        inputProps={{ maxLength: MESSAGE_MAX }}
                        helperText={`${message.length} / ${MESSAGE_MAX}`}
                        FormHelperTextProps={{ sx: { textAlign: 'right', m: 0, mt: 0.5 } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setFormOpen(false)} disabled={submitting} sx={{ textTransform: 'none', color: 'var(--brand-text-muted, #555)' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} disabled={submitting} sx={actionBtnSx}>
                        {submitting ? 'Sending…' : 'Send Message'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Support;
